import { Badge, Form, Popover, Space, theme } from 'antd'
import { FC, useCallback, useMemo, useState } from 'react'
import { EditableCellValue } from '../../../../components/editable-cell/elements'
import ShipmentCalculatedValue from '../../../../components/shipment-calculated-value/shipment-calculated-value'
import { getMaterialValidationIssues } from '../../../../helpers/entities/material/get-material-validation-result'
import {
    getShipmentMaterialConsumptions,
    hasShipmentMaterialConsumptions,
} from '../../../../helpers/entities/shipment/get-shipment-material-consumptions'
import { formatLayerLabel } from '../../../completed-print-jobs/helpers/consumption-helpers'
import { MaterialRequirement } from '../../../../types/api/components/material/material-requirements'
import MaterialRequirementsWrapper from '../shipment-form/components/form-items/material-requirements-wrapper/material-requirements-wrapper'
import { DataSourceItem } from '../../shipments-page'
import { getMaterialRequirementsFormInitialValues } from '../../helpers/get-material-requirements-form-initial-values'

export interface MaterialsColumnCellProps {
    value: MaterialRequirement[]
    record: DataSourceItem
}

/**
 * Ячейка «Материалы»: клик по содержимому как у редактируемой ячейки, своя форма в модалке
 * (не useFormInstance от формы таблицы). Сделано так из за слишком сложного поведения сохранения.
 * После фиксации расхода показывает фактические material_consumptions.
 */
export const MaterialsColumnCell: FC<MaterialsColumnCellProps> = ({ value: v, record }) => {
    const [form] = Form.useForm()
    const [open, setOpen] = useState(false)
    const hasActualConsumptions = hasShipmentMaterialConsumptions(record)
    const consumptions = useMemo(
        () => getShipmentMaterialConsumptions(record),
        [record]
    )
    const isEditable = !Boolean(record.print_job) && !hasActualConsumptions
    const { token } = theme.useToken()
    const validationIssues = useMemo(() => getMaterialValidationIssues(record), [record])

    const openEditor = useCallback(() => {
        if (!isEditable) return

        form.setFieldsValue({
            materialRequirements: getMaterialRequirementsFormInitialValues(record) ?? [],
        })
        setOpen(true)
    }, [form, isEditable, record])

    const plannedLabel = v?.[0]?.material_type?.name
    const actualLabel = consumptions[0]?.material?.material_type?.name
    const displayLabel = hasActualConsumptions ? actualLabel : plannedLabel
    const badgeCount = hasActualConsumptions
        ? consumptions.length
        : v?.length || 0

    return (
        <>
            <EditableCellValue onClick={isEditable ? openEditor : undefined}>
                <Popover
                    content={
                        <Space direction={'vertical'}>
                            {hasActualConsumptions
                                ? consumptions.map(consumption => (
                                      <div key={consumption.documentId || consumption.id}>
                                          <div style={{ fontWeight: 'bold' }}>
                                              {consumption.material?.material_type?.name || '—'}
                                          </div>
                                          <span>
                                              <b>{consumption.material?.width ?? '—'}</b>{' '}
                                              {consumption.amount ?? '—'}км -{' '}
                                              {formatLayerLabel(consumption.layerNumber)}
                                          </span>
                                      </div>
                                  ))
                                : (v || []).map(item => (
                                      <div key={item.id}>
                                          <div style={{ fontWeight: 'bold' }}>
                                              {item.material_type?.name}
                                          </div>
                                          <Space direction={'vertical'}>
                                              {item.configurations?.map(config => (
                                                  <span key={config.id}>
                                                      <b>{config.material?.width}</b> {config.amount}
                                                      км - {config.action}
                                                  </span>
                                              ))}
                                          </Space>
                                      </div>
                                  ))}
                            {!hasActualConsumptions &&
                                validationIssues.map(issue => (
                                    <div
                                        key={issue.message}
                                        style={{
                                            color: token.colorErrorText || token.colorError,
                                            backgroundColor: token.colorErrorBg,
                                        }}
                                    >
                                        {issue.message}
                                    </div>
                                ))}
                        </Space>
                    }
                >
                    <ShipmentCalculatedValue isActual={hasActualConsumptions}>
                        {badgeCount > 1 ? <Badge count={badgeCount} /> : null}{' '}
                        {displayLabel || '—'}
                    </ShipmentCalculatedValue>
                </Popover>
            </EditableCellValue>
            {open && (
                <MaterialRequirementsWrapper
                    record={record}
                    form={form}
                    open={open}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    )
}
